// Synced with opp-services pay/front-end CardForm/cardTheme.ts
// (OPP-16431 error icon, OPP-16634 themes, OPP-16729 tab order, OPP-17312 responsive iframe)

const errorIcon = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath fill='%23d20e1c' fill-rule='evenodd' d='M12 0c6.627 0 12 5.373 12 12s-5.373 12-12 12S0 18.627 0 12 5.373 0 12 0m0 16a2 2 0 1 0 0 4 2 2 0 0 0 0-4m0-13c-.552 0-1 .492-1 1.1v8.8c0 .608.448 1.1 1 1.1s1-.492 1-1.1V4.1c0-.608-.448-1.1-1-1.1'/%3E%3C/svg%3E")`

/**
 * Per-theme colours for the Evervault iframe. The iframe can't inherit the
 * host page CSS variables, so the palette is passed in explicitly.
 * Unknown themes (e.g. `sundeals`) fall back to `tui-light`.
 */
export const THEME_PALETTES = {
  tui: {
    text: 'hsl(217deg 83% 20%)',
    focus: 'hsl(199deg 74% 39%)',
    error: 'hsl(358deg 88% 44%)',
  },
  'tui-light': {
    text: 'hsl(248deg 69% 21%)',
    focus: 'hsl(224deg 91% 59%)',
    error: 'hsl(358deg 88% 44%)',
  },
  ltur: {
    text: 'hsl(330deg 100% 40%)',
    focus: 'hsl(330deg 100% 40%)',
    error: 'hsl(353deg 100% 55%)',
  },
  airtours: {
    text: 'hsl(0deg 0% 0%)',
    focus: 'hsl(178deg 95% 42%)',
    error: 'hsl(358deg 88% 44%)',
  },
  firstchoice: {
    text: 'hsl(149deg 100% 9%)',
    focus: 'hsl(149deg 100% 9%)',
    error: 'hsl(4deg 79% 58%)',
  },
  robinson: {
    text: 'hsl(0deg 0% 45%)',
    focus: 'hsl(209deg 100% 36%)',
    error: 'hsl(7deg 79% 58%)',
  },
  tuimagiclife: {
    text: 'hsl(0deg 0% 0%)',
    focus: 'hsl(190deg 59% 62%)',
    error: 'hsl(358deg 88% 44%)',
  },
  crystalski: {
    text: 'hsl(181deg 60% 28%)',
    focus: 'hsl(287deg 51% 40%)',
    error: 'hsl(341deg 98% 38%)',
  },
  vipselection: {
    text: 'hsl(200deg 38% 33%)',
    focus: 'hsl(199deg 74% 39%)',
    error: 'hsl(358deg 88% 44%)',
  },
}

export const DEFAULT_THEME = 'tui-light'

const BORDER_COLOR = 'hsl(217deg 7% 64%)'
const FONT_FAMILY = 'Ambit, sans-serif'

/**
 * Build the Evervault <Card> theme for the given MFE theme name.
 * @param {string} [theme] MFE theme (`data-theme` value)
 * @returns {{ styles: Object }}
 */
export const getCardTheme = (theme = DEFAULT_THEME) => {
  const palette = THEME_PALETTES[theme] ?? THEME_PALETTES[DEFAULT_THEME]
  const focusGlow = palette.focus.replace(/\)$/, ' / 40%)')

  return {
    styles: {
      ':root': { colorScheme: 'light' },
      '#root': {
        width: '100%',
        maxWidth: 340,
        paddingLeft: 6,
        paddingRight: 6,
        paddingBottom: 6,
        boxSizing: 'border-box',
      },
      label: {
        fontSize: 16,
        color: palette.text,
        fontFamily: FONT_FAMILY,
        lineHeight: 1.5,
        textTransform: 'none',
        paddingTop: 8,
        paddingBottom: 8,
        paddingLeft: 0,
        display: 'block',
        cursor: 'pointer',
        userSelect: 'none',
      },
      img: { marginLeft: 20, position: 'absolute', top: '47px', right: '20px' },
      '.error': {
        color: palette.error,
        fontSize: 14,
        marginTop: 4,
        fontFamily: FONT_FAMILY,
      },
      '.error::before': {
        content: '""',
        display: 'inline-block',
        verticalAlign: 'text-bottom',
        marginRight: 4,
        width: 16,
        height: 16,
        backgroundImage: errorIcon,
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'contain',
      },
      '.field': {
        borderRadius: 0,
        width: '100%',
        boxSizing: 'border-box',
        position: 'relative',
        overflow: 'visible',
      },
      // No CSS `order` on fields: it breaks keyboard tab order (OPP-16729).
      // Visual order comes from the `fields` prop instead.
      '[ev-component=card]': {
        display: 'flex',
        flexDirection: 'column',
        overflow: 'visible',
      },
      '.field input': {
        padding: '0 15px',
        border: `1px solid ${BORDER_COLOR}`,
        borderRadius: 5,
        fontSize: 16,
        color: palette.text,
        fontFamily: FONT_FAMILY,
        lineHeight: 1.5,
        height: 39,
        outline: 'none',
      },
      '.field:focus-within input': {
        borderColor: palette.focus,
        boxShadow: `0 0 0 2px rgb(255 255 255), 0 0 0 4px ${palette.focus}, 0 0 0 6px ${focusGlow}`,
      },
      '.field[ev-name=expiry] input, .field[ev-name=cvc] input': {
        width: '100%',
        maxWidth: 170,
      },
      '.field[ev-valid=false] input': {
        borderColor: palette.error,
      },
    },
  }
}
